/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./FilterBaseController',
	'./DateTimeFilterView',
	'./DateTimeFilterModel',
	'underscore'
], function (BaseClass, FilterView, FilterModel, _) {
	'use strict';

	/**
	 * The controller holds the business logic for the date time filter
	 */
	var DateTimeFilterController = BaseClass.extend({

		init: function () {
			DateTimeFilterController.inherited('init', this, arguments);
			this.id = _.uniqueId('DateTimeFilterController');
			this.itemContext.eventSourceId = this.id;
			this.ready = Promise.resolve(null);
		},

		initialize: function() {
			return this._initializeModelAndView();
		},

		_initializeModelAndView: function() {
			this._initializeExtraData();
			this._createFilterModel();
			this._createFilterView();
			this.setLoading();
			this._loadMinMax().then(this.onInitializationDone.bind(this));
			return Promise.resolve();
		},

		_initializeExtraData: function () {
			this._initializePageContext(/*isRange*/true);
		},

		_createFilterModel: function() {
			this.model = new FilterModel({});
			this._setModelFromPersistedValues();
		},

		_createFilterView: function() {
			this.viewConfig = _.extend(this.viewConfig, {
				model: this.model,
				dataType: this._getItemDataType(),
				cbOnChange: this.onChange.bind(this),
				cbOnClear: this.onClear.bind(this),
				cbOnApply: this.onApply.bind(this),
				cbOnClose: this.onClose.bind(this),
				cbOnDone: this.onDone.bind(this)
			});

			this.view = new FilterView(this.viewConfig);
		},

		_getCurrentMinMaxArray: function () {
			var result = [];
			var state = this.model.toJSON();
			result.push(state.min, state.max);
			return result;
		},

		_setModelFromPersistedValues: function(){
			var name = this._getDialogCaption();
			var inverted = this._getSelectionIsInverted();
			this.model.setName(name);
			this.model.setInvert(inverted);
		},

		_getDialogCaption: function(){
			var pageContextItem = this.getCurrentPageContextItem();
			return pageContextItem ? pageContextItem.getItemName() : this.itemContext.itemName;
		},

		_getSelectionIsInverted: function(){
			var pageContextItem = this.getCurrentPageContextItem();
			return pageContextItem ? pageContextItem.getExclude() : false;
		},

		_getItemDataType: function(){
			var pageContextItem = this.getCurrentPageContextItem();
			return pageContextItem ? pageContextItem.getDataType().toLowerCase(): 'date';
		},

		_updateFilter: function(state){
			this.pageContextApi.updateFilterContext(this.itemContext, {
				command: 'update',
				condition: {
					from: state.from,
					to: state.to,
					invert: state.inverted
				}
			});
		},

		_removeFilter: function(){
			this.pageContextApi.updateFilterContext(this.itemContext, { command: 'clearValues' });
		},

		onOpen: function() {
			this.setLoading();
			if (this._ensureFilterViewExists()) {
				// New view or model is created.
				this.view.renderViewComposition()
					.then(this._loadMinMax.bind(this))
					.then(this.removeLoading.bind(this));
			} else {
				this._refreshModel(this._getCurrentMinMaxArray());
				this.removeLoading();
			}
		},

		onChange: function (payload) {
			var minMaxArray = this._getCurrentMinMaxArray();
			this.model.update(payload.fromValue, payload.toValue, minMaxArray[0], minMaxArray[1], payload.rangeType, payload.inverted);
		},

		onClear: function () {
			this.model.clearValues();
		},

		onDone: function() {
			var state = this.model.toJSON();
			if (state.rangeType === 'before') {
				state.from = '';
			} else if (state.rangeType === 'after') {
				state.to = '';
			}
			// if from & to are the same as the min & max then this implies "no filter"
			var minMaxArray = this._getCurrentMinMaxArray();
			if(state.from !== minMaxArray[0] || state.to !== minMaxArray[1] || state.inverted) {
				this._updateFilter(state);
			} else {
				this._removeFilter();
			}

			if (!this.view.isCompact) {
				this.view.remove();
			}
		},

		_getSelection: function(){
			var pageContextItem = this.getCurrentPageContextItem();
			if(pageContextItem){
				return {
					from: pageContextItem.getFrom(),
					to: pageContextItem.getTo(),
					inverted: pageContextItem.getInvert()
				};
			}
		},

		_refreshModel: function(minMaxArray) {
			minMaxArray = minMaxArray ? minMaxArray : [];

			if (!this.model) {
				return;
			}

			var from, to, min, max, rangeType, inverted;

			if(minMaxArray.length === 2) {
				min = minMaxArray[0];
				max = minMaxArray[1];
			} else {
				min = '';
				max = '';
			}

			var selection = this._getSelection();
			if(selection) {
				from = selection.from;
				to = selection.to;
				inverted = selection.inverted;

				if (from === '' && to === '') {
					rangeType = 'between';
					from = min;
					to = max;
				} else if (from === '') {
					rangeType = 'before';
					from = min;
				} else if (to === '') {
					rangeType = 'after';
					to = max;
				} else if (inverted) {
					rangeType = 'not between';
				} else {
					rangeType = 'between';
				}
			} else {
				from = min;
				to = max;
				rangeType = 'between';
				inverted = false;
			}
			this.model.update(from, to, min, max, rangeType, inverted);
		},

		render: function () {
			this._refreshModel(this._getCurrentMinMaxArray());
			return this.view.renderViewComposition()
				.catch(this.logger.error.bind(this.logger));
		},

		/**
		 * This function is called by processContextChange if page context change impacts current filter.
		 * @return {Promise}
		 */
		_updateFilterDataOnPageContextChange: function () {
			return this._loadMinMax();
		},

		_loadMinMax: function() {
			return this.queryHelper.getMinMax()
				.then(function(minMaxArray) {
					this._refreshModel(minMaxArray);
				}.bind(this))
				.catch(function(e) {
					this.processError(e);
				}.bind(this));
		},

		refreshDataAndView: function() {
			return this._loadMinMax().then(function() {
				return this.view.renderViewComposition();
			}.bind(this));
		}
	});
	return DateTimeFilterController;
});
